import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaRocket, 
  FaUsers, 
  FaAward, 
  FaHandshake, 
  FaChartLine,
  FaLightbulb,
  FaMobile,
  FaCloud,
  FaShieldAlt,
  FaClock,
  FaDollarSign,
  FaArrowRight,
  FaCheckCircle,
  FaGem,
  FaStar,
  FaQuoteLeft,
  FaLaptopCode,
  FaHeart,
  FaGlobeAmericas,
  FaHeadset
} from 'react-icons/fa';
import { seoImage } from '../config/pageSeoConfig';
import './WhoWeAre.scss';

const companyValues = [
  {
    icon: FaLightbulb,
    title: 'Innovation First',
    description: 'We constantly explore cutting-edge technologies and methodologies to deliver solutions that give you a competitive edge in the digital landscape.'
  },
  {
    icon: FaUsers,
    title: 'Client-Centric Approach',
    description: 'Your success is our success. We prioritize understanding your unique needs and goals to create tailored solutions that drive real business results.'
  },
  {
    icon: FaAward,
    title: 'Quality Excellence',
    description: 'We maintain the highest standards in every project, ensuring clean code, robust architecture, and exceptional user experiences that stand the test of time.'
  },
  {
    icon: FaHandshake,
    title: 'Transparent Partnership',
    description: 'Open communication, honest timelines, and collaborative decision-making. We believe in building lasting relationships based on trust and mutual respect.'
  },
  {
    icon: FaRocket,
    title: 'Results-Driven',
    description: 'We focus on measurable outcomes—increased conversions, improved performance, and enhanced user engagement that directly impact your bottom line.'
  },
  {
    icon: FaHeart,
    title: 'Passionate Craftsmanship',
    description: 'We love what we do, and it shows. Every line of code, every design element is crafted with dedication and attention to detail.'
  }
];

const whatWeOffer = [
  {
    icon: FaLaptopCode,
    title: 'Custom Web Applications',
    description: 'Scalable, high-performance web applications built with modern frameworks like React, Next.js, and Node.js.',
    features: ['Progressive Web Apps', 'Real-time Features', 'Cloud Integration', 'Performance Optimization']
  },
  {
    icon: FaMobile,
    title: 'Mobile App Development',
    description: 'Native and cross-platform mobile solutions that deliver exceptional user experiences on iOS and Android.',
    features: ['React Native', 'Flutter', 'Native Development', 'App Store Publishing']
  },
  {
    icon: FaCloud,
    title: 'Cloud & DevOps',
    description: 'Scalable cloud infrastructure and automated CI/CD pipelines for efficient, reliable deployments.',
    features: ['AWS/Azure/GCP', 'Docker & Kubernetes', 'CI/CD Automation', 'Infrastructure as Code']
  },
  {
    icon: FaGem,
    title: 'UI/UX Design',
    description: 'Beautiful, intuitive interfaces that delight users and drive engagement through thoughtful design.',
    features: ['User Research', 'Wireframing', 'Prototyping', 'Design Systems']
  }
];

const stats = [
  { number: '10+', label: 'Years of Experience', icon: FaAward },
  { number: '80+', label: 'Projects Delivered', icon: FaRocket },
  { number: '50+', label: 'Happy Clients', icon: FaUsers },
  { number: '10+', label: 'Industries Served', icon: FaGlobeAmericas }
];

const whyChooseUs = [
  {
    icon: FaShieldAlt,
    title: 'Enterprise-Grade Security',
    description: 'Bank-level security with SSL encryption, JWT authentication, and regular security audits to protect your data and users.'
  },
  {
    icon: FaClock,
    title: 'On-Time Delivery',
    description: 'We respect your deadlines and deliver projects on schedule with transparent communication throughout the process.'
  },
  {
    icon: FaDollarSign,
    title: 'Cost-Effective Solutions',
    description: 'Premium quality development at competitive rates with transparent pricing and no hidden costs.'
  },
  {
    icon: FaChartLine,
    title: 'Scalable Architecture',
    description: 'Future-proof solutions built with microservices and cloud infrastructure that grow seamlessly with your business.'
  },
  {
    icon: FaHeadset,
    title: '24/7 Support',
    description: 'Comprehensive maintenance, security updates, and ongoing support to keep your applications running smoothly.'
  },
  {
    icon: FaCheckCircle,
    title: 'Proven Track Record',
    description: '95%+ client satisfaction rate with numerous successful projects across diverse industries and technologies.'
  }
];

const testimonials = [
  {
    text: "App Developer Pro transformed our digital presence with a stunning, high-performance web application. Their expertise and dedication exceeded our expectations.",
    author: "Sarah Johnson",
    company: "Tech Innovations Inc.",
    position: "CEO",
    rating: 5
  },
  {
    text: "Working with App Developer Pro was seamless. They understood our vision and delivered a solution that perfectly aligned with our business goals.",
    author: "Michael Chen",
    company: "Global Solutions Ltd.",
    position: "CTO",
    rating: 5
  },
  {
    text: "The quality of work and attention to detail is outstanding. They're not just developers—they're true partners in our success.",
    author: "Emily Rodriguez",
    company: "Digital Marketing Agency",
    position: "Founder",
    rating: 5
  }
];

const WhoWeAre: React.FC = () => {
  // SEO constants
  const seoTitle = "Who We Are - App Developer Pro | Your Digital Transformation Partner";
  const seoDescription = "Learn about App Developer Pro - a leading web and mobile app development company delivering innovative, scalable digital solutions. 10+ years of experience, 80+ projects, and 50+ satisfied clients worldwide.";
  const seoKeywords = "app developer pro, web development company, mobile app development, software development services, custom app development, digital transformation, about us";
  const seoUrl = "https://appdeveloperpro.com/who-we-are";
  
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Who We Are - App Developer Pro",
    "description": seoDescription,
    "url": seoUrl,
    "mainEntity": {
      "@type": "Organization",
      "@id": "https://appdeveloperpro.com/#organization",
      "name": "App Developer Pro",
      "description": "Professional web and mobile application development services",
      "url": "https://appdeveloperpro.com",
      "foundingDate": "2014",
      "numberOfEmployees": "10-50",
      "areaServed": "Worldwide"
    }
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={seoImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <div className="who-we-are">
        <section className="who-we-are__hero">
          <div className="who-we-are__hero-content">
            <h1>Building Digital Excellence Since 2014</h1>
            <p>
              We are <strong>App Developer Pro</strong> — a passionate team dedicated to transforming ideas into powerful, scalable digital solutions that drive business growth and innovation.
            </p>
          </div>
        </section>

        {/* Stats Section */}
        <section className="who-we-are__stats">
          <div className="who-we-are__container">
            <div className="who-we-are__stats-grid">
              {stats.map((stat, index) => {
                const StatIcon = stat.icon;
                return (
                  <motion.div
                    key={stat.label}
                    className="who-we-are__stat-item"
                    initial={{ opacity: 0, scale: 0.5 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="who-we-are__stat-header">
                      <div className="who-we-are__stat-icon">
                        <StatIcon />
                      </div>
                      <div className="who-we-are__stat-content">
                        <div className="who-we-are__stat-number">{stat.number}</div>
                        <div className="who-we-are__stat-label">{stat.label}</div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="who-we-are__story">
          <div className="who-we-are__container">
            <motion.div
              className="who-we-are__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="who-we-are__section-title">Our Story</h2>
              <div className="who-we-are__story-content">
                <p>
                  <strong>App Developer Pro</strong> was founded with a simple yet powerful vision: to help businesses harness the power of technology to achieve their goals. What started as a passion for crafting exceptional digital experiences has evolved into a full-service development agency trusted by clients worldwide.
                </p>
                <p>
                  Over the past decade, we've helped startups launch innovative products, assisted enterprises in digital transformation, and partnered with businesses of all sizes to build solutions that matter. From e-commerce platforms to complex enterprise applications, we've delivered <strong>80+ successful projects</strong> across <strong>10+ industries</strong>.
                </p>
                <p>
                  Our approach is rooted in collaboration, transparency, and a relentless focus on quality. We don't just build applications—we create digital experiences that engage users, drive conversions, and generate measurable business results. Every project is an opportunity to push boundaries and deliver excellence.
                </p>
                <p>
                  Today, <strong>App Developer Pro</strong> stands as a trusted partner for businesses seeking cutting-edge web and mobile solutions. Our team combines technical expertise with creative innovation to deliver applications that are not only functional but exceptional.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="who-we-are__values">
          <div className="who-we-are__container">
            <motion.div
              className="who-we-are__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="who-we-are__section-title">Our Core Values</h2>
              <p className="who-we-are__section-subtitle">
                The principles that guide everything we do
              </p>
            </motion.div>
            <div className="who-we-are__values-grid">
              {companyValues.map((value, index) => {
                const ValueIcon = value.icon;
                return (
                  <motion.div
                    key={value.title}
                    className="who-we-are__value-card"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -10 }}
                  >
                    <div className="who-we-are__value-header">
                      <div className="who-we-are__value-icon">
                        <ValueIcon />
                      </div>
                      <h3 className="who-we-are__value-title">{value.title}</h3>
                    </div>
                    <p className="who-we-are__value-description">{value.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* What We Offer Section */}
        <section className="who-we-are__offerings">
          <div className="who-we-are__container">
            <motion.div
              className="who-we-are__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="who-we-are__section-title">What We Offer</h2>
              <p className="who-we-are__section-subtitle">
                Comprehensive digital solutions tailored to your needs
              </p>
            </motion.div>
            <div className="who-we-are__offerings-grid">
              {whatWeOffer.map((offering, index) => {
                const OfferingIcon = offering.icon;
                return (
                  <motion.div
                    key={offering.title}
                    className="who-we-are__offering-card"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="who-we-are__offering-header">
                      <div className="who-we-are__offering-icon">
                        <OfferingIcon />
                      </div>
                      <h3 className="who-we-are__offering-title">{offering.title}</h3>
                    </div>
                    <p className="who-we-are__offering-description">{offering.description}</p>
                    <ul className="who-we-are__offering-features">
                      {offering.features.map((feature) => (
                        <li key={feature}>
                          <FaCheckCircle /> {feature}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="who-we-are__why-choose">
          <div className="who-we-are__container">
            <motion.div
              className="who-we-are__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="who-we-are__section-title">Why Choose App Developer Pro?</h2>
              <p className="who-we-are__section-subtitle">
                What sets us apart from other development agencies
              </p>
            </motion.div>
            <div className="who-we-are__why-choose-grid">
              {whyChooseUs.map((reason, index) => {
                const ReasonIcon = reason.icon;
                return (
                  <motion.div
                    key={reason.title}
                    className="who-we-are__why-choose-card"
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="who-we-are__why-choose-header">
                      <div className="who-we-are__why-choose-icon">
                        <ReasonIcon />
                      </div>
                      <h3 className="who-we-are__why-choose-title">{reason.title}</h3>
                    </div>
                    <p className="who-we-are__why-choose-description">{reason.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="who-we-are__testimonials">
          <div className="who-we-are__container">
            <motion.div
              className="who-we-are__section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="who-we-are__section-title">What Our Clients Say</h2>
              <p className="who-we-are__section-subtitle">
                Real feedback from real clients who trust us
              </p>
            </motion.div>
            <div className="who-we-are__testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.author}
                  className="who-we-are__testimonial-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="who-we-are__testimonial-quote">
                    <FaQuoteLeft />
                  </div>
                  <div className="who-we-are__testimonial-stars">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <FaStar key={i} />
                    ))}
                  </div>
                  <p className="who-we-are__testimonial-text">"{testimonial.text}"</p>
                  <div className="who-we-are__testimonial-author">
                    <div>
                      <strong>{testimonial.author}</strong>
                      <span>{testimonial.position}, {testimonial.company}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="who-we-are__cta">
          <div className="who-we-are__container">
            <motion.div
              className="who-we-are__cta-content"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="who-we-are__cta-title">Ready to Start Your Project?</h2>
              <p className="who-we-are__cta-text">
                Let's discuss how we can help transform your ideas into powerful digital solutions. Get in touch with us today for a free consultation.
              </p>
              <div className="who-we-are__cta-buttons">
                <Link to="/contact" className="who-we-are__btn who-we-are__btn--primary">
                  Get Free Consultation <FaArrowRight />
                </Link>
                <Link to="/pricing" className="who-we-are__btn who-we-are__btn--secondary">
                  View Pricing
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WhoWeAre;

